import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Stack, Typography } from '@mui/material';
import ContainerBox from 'components/ContainerBox';
import { OnlineLayoutSettings } from 'common/modules/customization';
import { useTranslation } from 'services/localization';
import * as ShopSelectors from 'selectors/ShopSelectors';
import FooterContent from 'common/components/customizer/FooterContent';
import { isLitePlan } from 'common/modules/plans';
import { CustomizerPage, PAGES } from '../constants';
import HomePreview from './HomePreview';
import AboutPreview from './AboutPreview';
import CenteredContent from '../components/CenteredContent';

export interface Props {
	selectedPage: CustomizerPage;
	layoutSettings: OnlineLayoutSettings;
}

const CustomizerPreview = (props: Props) => {
	const { layoutSettings, selectedPage } = props;
	const { footer, about, header } = layoutSettings;
	const { t } = useTranslation();
	const shopLogo = useSelector(ShopSelectors.shopLogo);
	const shopInfo = useSelector(ShopSelectors.activeShopPublicInfo)!;
	const lang = useSelector(ShopSelectors.activeShopDefaultLanguage);
	const shopPlan = useSelector(ShopSelectors.shopPlan);
	const isLiteShop = isLitePlan(shopPlan);

	const renderSelectedPage = (selectedPage: CustomizerPage) => {
		switch (selectedPage) {
			case PAGES.general:
			case PAGES.home:
				return <HomePreview layoutSettings={layoutSettings} />;
			case PAGES.about:
				return <AboutPreview about={about} />;
			default:
				return undefined;
		}
	};

	return (
		<ContainerBox padding="none">
			<CenteredContent>
				<Stack sx={{ px: 0, pt: 3 }}>
					<Stack
						sx={{ mx: 'auto', width: '100%' }}
						direction={header?.logo.placement === 'left' ? 'row' : 'column'}
						alignItems="center"
					>
						<Box>
							{shopLogo && (
								<img
									style={{
										width: header.logo.width,
										objectFit: 'contain',
										objectPosition: 'center',
									}}
									src={shopLogo}
									alt="Shop logo"
								/>
							)}
						</Box>
						<Stack direction="row" justifyContent="space-evenly">
							<Typography sx={{ fontWeight: '500' }} m={2}>
								{t('channels.online.customizer.home', 'Home')}
							</Typography>
							<Typography sx={{ fontWeight: '500' }} m={2}>
								{t('channels.online.customizer.about', 'About')}
							</Typography>
							<Typography sx={{ fontWeight: '500' }} m={2}>
								{t('channels.online.customizer.stores', 'Stores')}
							</Typography>
						</Stack>
					</Stack>
					{renderSelectedPage(selectedPage)}
				</Stack>
			</CenteredContent>
			<FooterContent
				footerInfo={footer}
				paymentMethods={shopInfo.activePaymentMethods}
				shopInfoString={shopInfo.name}
				language={lang}
				t={t}
				isLiteShop={isLiteShop}
			/>
		</ContainerBox>
	);
};

export default CustomizerPreview;
